import React, {useContext, useState, useEffect} from "react";
import Context from "../ProductsProvider";
import styled from "@emotion/styled";
import Filters from "./Filters";
import ClearAllButton from "./ClearAllButton";
import Search from "./Search";
import { useMediaQuery } from "react-responsive";

//style rules
import { Sizes, Colors } from "../../../styles";

const FilterGroups = () => {
	const { data: { language, filterGroups, dialog },
		actions: { setDialogIsOpen },
	} = useContext(Context);

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.large})`,
	});

	const [isExpanded, setIsExpanded] = useState([]);
	
	useEffect(() => {
		// we want to have all filter groups expanded initially so we're setting
		// all the isExpanded initial state to true
		const expandedGroups = Array(filterGroups.length).fill(true, 0);
		setIsExpanded(expandedGroups);
	}, [filterGroups]);

	const FiltersTitleText = () => {
		if (language === "en") return "Filter products";
		return "Filtrer les produits";
	};

	const legendText = () => {
		if (language === "en") return "Shop by";
		return "Magasine par";
	}

	const showResultsText = () => {
		if (language === "en") return "Show results";
		return "Afficher les résultats";
	}

	const handleCloseFiltersButtonClick = (dialog) => {
		setDialogIsOpen(false);
		dialog.close();
	};

	const handleFilterGroupToggleButtonClick = (index) => {
		setIsExpanded(currentValue => {
			const arrayCopy = [...currentValue];
			
			if (currentValue[index]) {
				arrayCopy[index] = null;
				return arrayCopy;
			} else {
				arrayCopy[index] = true;
				return arrayCopy;
			}
		});
	};

	const closeBtnAltText = () => {
		if (language === "en") return "close filter menu";
		return "fermer le menu de filtres";
	}

	if (filterGroups.length === 0) return null;

	return (
		<FiltersContainer>
			<FiltersWrapper ref={dialog} as={isDesktopOrLaptop ? "div" : "dialog"}>
				{
					isDesktopOrLaptop ? null
						: <>
							<CloseButton
								type="button"
								aria-label={closeBtnAltText()}
								onClick={() => handleCloseFiltersButtonClick(dialog.current)}
							>
								<img src={`${mainScript.assetPath}images/svg/icons/icon-close-green.svg`} alt="" />
							</CloseButton>
							<FiltersTitle>{FiltersTitleText()}</FiltersTitle>
							<FiltersTitleIcon src={`${mainScript.assetPath}images/svg/icons/icon-filters.svg`} alt="" />
							<Search />
						</>
				}
				<FiltersList>
					{
						filterGroups.map( (filterGroup, index) => {
							return (
								<FiltersListItem
									key={filterGroup.slug}
								>
									<Fieldset>
										<FieldsetHeader>
											<FieldsetLegend>
												{legendText()} {filterGroup.title.toLowerCase()}
											</FieldsetLegend>
											{
												isDesktopOrLaptop ? null
													: <FilterGroupToggleButton
														type="button"
														onClick={() => handleFilterGroupToggleButtonClick(index)}
														expanded={isExpanded[index]}
													>
														<img src={`${mainScript.assetPath}images/svg/icons/down-arrow-black.svg`} alt="" />
													</FilterGroupToggleButton>
											}
										</FieldsetHeader>
										<Filters
											filters={filterGroup.filters}
											category={filterGroup.slug}
											groupIndex={index}
											isExpanded={isExpanded}
										/>
									</Fieldset>
								</FiltersListItem>
							);
						})
					}
				</FiltersList>
				{
					isDesktopOrLaptop ? null
						: <ButtonsContainer>
							<ShowResultsButton
								type="button"
								onClick={() => handleCloseFiltersButtonClick(dialog.current)}
							>
								{showResultsText()}
							</ShowResultsButton>
							<ClearAllButton isDesktop={isDesktopOrLaptop} />
						</ButtonsContainer>
				}
			</FiltersWrapper>
		</FiltersContainer>
	)
}

export default FilterGroups;

const FiltersContainer = styled.div`
`;

const FiltersWrapper = styled.dialog`
	position: fixed;
	top:0;
	left:0;
	background-color: ${Colors.base.white};
	z-index: 55;
	padding: 1.5rem 1rem 3rem 1rem;
	border: unset;
	margin: unset;
	height: 100%;
	max-height: 100vh;
	min-width: 31.5rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		position: static;
		padding: 0;
		max-height: unset;
		min-width: unset;
	}

	&::backdrop {
		background-color: rgba(0, 0, 0, 0.6);
	}
`;

const CloseButton = styled.button`
	all: unset;
	width: 2.2rem;
	height: 2.2rem;
    display: block;
	cursor: pointer;
	margin-left: auto;

	img {
		display: block;
		height: 100%;
		width: 100%;
		object-fit: contain;
`;

const FiltersTitle = styled.h4`
	width: fit-content;
	display: inline-block;
`;

const FiltersTitleIcon = styled.img`
	display: inline-block;
	height: 2.4rem;
	width: 2.4rem;
	position: relative;
	top: 0.5rem;
	margin-left: 1rem;
`;

const FiltersList = styled.ul`
	list-style: none;
	padding: 0 4.4rem 0 0;
	margin: unset;
	margin-top: 1rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		padding: 0;
		margin-top: 0;
	}
`;

const FiltersListItem = styled.li`
	padding: 3rem 0;
	margin: unset;
	border-bottom: 0.1rem solid ${Colors.fp.pineGreen60};

	&:last-of-type {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			border-bottom: unset;
		}
	}

	&:first-of-type {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			padding-top: 0;
		}
	}
`;

const FilterGroupToggleButton = styled.button`
	all: unset;
	height: 2rem;
	width: 2rem;
	display: block;
	cursor: pointer;

	img {
		display: block;
		height: 0.94rem;
		width: 1.6rem;
		object-fit: contain;
		position: relative;
		transform: ${props => props.expanded ? "translate(0.2rem, 0.1rem) rotate(180deg)" : "translate(0.2rem, 0.1rem)"};
	}
`;

const Fieldset = styled.fieldset`
	border: none;
	padding: 0;
	margin: 0;
`;

const FieldsetHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const FieldsetLegend = styled.legend`
	padding: 0 1.5rem;
`;

const ShowResultsButton = styled.button`
`;

const ButtonsContainer = styled.div`
	margin-top: 3rem;
`;
